/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ArrowLeft, Bell, Moon, Plus, Search, Sun } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { type ChangeEvent, useCallback, useMemo, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useMe } from '../../lib/queries/me.js';
import {
  useMarkAllNotificationsRead,
  useNotifications,
  useUnreadCount,
} from '../../lib/queries/notifications.js';
import { uiStore } from '../../lib/stores/ui-store.js';
import { cn } from '../../lib/utils/cn.js';

import type { NavItem } from './nav-config.js';

export interface AddAction {
  readonly labelKey: string;
  readonly to: string;
}

interface TopBarProps {
  readonly nav: readonly NavItem[];
  readonly addActions: readonly AddAction[];
  /** Staff console forces dark mode, so its manual light/dark toggle is hidden. */
  readonly forcedDark: boolean;
}

const ICON_BTN =
  'focus-ring relative flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700';
const MENU_CONTENT =
  'z-50 min-w-[12rem] rounded-xl border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-700 dark:bg-slate-800';

const SearchBox = observer(function SearchBox(): JSX.Element {
  const { t } = useTranslation();
  const onChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    uiStore.setSearch(event.target.value);
  }, []);
  return (
    <div className="relative hidden flex-1 sm:block">
      <Search
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
        aria-hidden="true"
      />
      <input
        type="search"
        aria-label={t('common.search')}
        placeholder={t('common.search')}
        value={uiStore.search}
        onChange={onChange}
        className="focus-ring h-10 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-4 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
      />
    </div>
  );
});

const ThemeToggle = observer(function ThemeToggle(): JSX.Element {
  const { t } = useTranslation();
  const toggle = useCallback(() => {
    uiStore.toggleTheme();
  }, []);
  const dark = uiStore.theme === 'dark';
  return (
    <button
      type="button"
      onClick={toggle}
      className={ICON_BTN}
      aria-label={t('topbar.toggleTheme')}
    >
      {dark ? (
        <Sun className="h-5 w-5" aria-hidden="true" />
      ) : (
        <Moon className="h-5 w-5" aria-hidden="true" />
      )}
    </button>
  );
});

function AddNewItem({ action }: { readonly action: AddAction }): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const onSelect = useCallback(() => {
    void navigate(action.to);
  }, [navigate, action.to]);
  return (
    <DropdownMenu.Item
      onSelect={onSelect}
      className="focus-ring cursor-pointer rounded-lg px-3 py-2 text-sm text-slate-700 outline-none hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700"
    >
      {t(action.labelKey)}
    </DropdownMenu.Item>
  );
}

function AddNewMenu({ actions }: { readonly actions: readonly AddAction[] }): JSX.Element | null {
  const { t } = useTranslation();
  if (actions.length === 0) {
    return null;
  }
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger className="focus-ring inline-flex h-10 items-center gap-2 rounded-xl bg-[var(--accent)] px-4 text-sm font-semibold text-white hover:bg-[var(--accent-hover)]">
        <Plus className="h-4 w-4" aria-hidden="true" />
        {t('topbar.addNew')}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={8} className={MENU_CONTENT}>
          {actions.map((action) => (
            <AddNewItem key={action.to} action={action} />
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

const NotificationsMenu = observer(function NotificationsMenu(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const unread = useUnreadCount();
  const recent = useNotifications();
  const markAll = useMarkAllNotificationsRead();
  const count = unread.data ?? 0;
  const items = (recent.data ?? []).slice(0, 6);
  const base = pathname.split('/')[1] ?? 'admin';

  const onViewAll = useCallback(() => {
    void navigate(`/${base}/inbox`);
  }, [navigate, base]);
  const onMarkAll = useCallback(() => {
    markAll.mutate();
  }, [markAll]);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger className={ICON_BTN} aria-label={t('topbar.notifications')}>
        <Bell className="h-5 w-5" aria-hidden="true" />
        {count > 0 && (
          <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">
            {count}
          </span>
        )}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={8} className={cn(MENU_CONTENT, 'w-80 p-0')}>
          <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2.5 dark:border-slate-700">
            <span className="text-sm font-semibold text-slate-800 dark:text-white">
              {t('inbox.title')}
            </span>
            {count > 0 && (
              <button
                type="button"
                onClick={onMarkAll}
                className="text-xs font-medium text-[var(--accent)]"
              >
                {t('inbox.markAllRead')}
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-slate-500 dark:text-slate-400">
              {t('inbox.empty')}
            </p>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              {items.map((n) => (
                <div
                  key={n.id}
                  className={cn(
                    'border-b border-slate-50 px-3 py-2.5 dark:border-slate-800',
                    n.readAt === null && 'bg-[var(--accent)]/5',
                  )}
                >
                  <p className="text-sm font-medium text-slate-800 dark:text-white">{n.title}</p>
                  {n.body !== null && (
                    <p className="truncate text-xs text-slate-500 dark:text-slate-400">{n.body}</p>
                  )}
                </div>
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={onViewAll}
            className="w-full border-t border-slate-100 px-3 py-2.5 text-center text-sm font-medium text-[var(--accent)] dark:border-slate-700"
          >
            {t('inbox.viewAll')}
          </button>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
});

/** Global top bar (Figma) shared by every portal screen. */
export const TopBar = observer(function TopBar({
  nav,
  addActions,
  forcedDark,
}: TopBarProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const me = useMe();

  const title = useMemo(() => {
    const match = nav
      .filter((item) => pathname === item.path || pathname.startsWith(`${item.path}/`))
      .sort((a, b) => b.path.length - a.path.length)[0];
    return match !== undefined ? t(`nav.${match.labelKey}`) : t('common.appName');
  }, [nav, pathname, t]);

  const goBack = useCallback(() => {
    void navigate(-1);
  }, [navigate]);

  const name = me.data?.fullName !== undefined && me.data.fullName !== '' ? me.data.fullName : '';

  return (
    <header className="flex h-20 items-center gap-4 border-b border-slate-200 bg-white px-6 dark:border-slate-700 dark:bg-slate-900">
      <button type="button" onClick={goBack} className={ICON_BTN} aria-label={t('topbar.back')}>
        <ArrowLeft className="h-5 w-5" aria-hidden="true" />
      </button>
      <div className="min-w-0">
        <h1 className="truncate text-xl font-bold text-slate-800 dark:text-white">{title}</h1>
        <p className="truncate text-xs text-slate-500 dark:text-slate-400">
          {name !== '' ? t('topbar.welcomeName', { name }) : t('topbar.welcome')}
        </p>
      </div>
      <div className="flex flex-1 items-center justify-end gap-3">
        <SearchBox />
        <NotificationsMenu />
        {!forcedDark && <ThemeToggle />}
        <AddNewMenu actions={addActions} />
      </div>
    </header>
  );
});
